import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, SlidersHorizontal, Sparkles, Plus, Star,
  TrendingUp, Clock, MapPin, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { fetchRecommendations, fetchRouteCreatorPermission, fetchRoutesPage } from '../../api/move';
import type { MoveRouteSummary, MoveRoutesPagination, Recommendations, RouteType } from '../../api/move';
import { API_BASE } from '../../config';
import styles from '../../styles/Move.module.css';

const TYPES: { key: RouteType | 'all'; label: string }[] = [
  { key: 'all', label: 'Todas' },
  { key: 'walk', label: 'Caminar' },
  { key: 'run', label: 'Correr' },
  { key: 'bike', label: 'Bici' },
];

type SortKey = 'popular' | 'rating' | 'new';

function formatDistance(m: number) {
  if (m >= 1000) return `${(m / 1000).toFixed(1)} km`;
  return `${m} m`;
}

function difficultyLabel(d: number) {
  if (d <= 1) return 'Muy fácil';
  if (d === 2) return 'Fácil';
  if (d === 3) return 'Moderada';
  if (d === 4) return 'Difícil';
  return 'Exigente';
}

function imageUrl(filename?: string | null) {
  if (!filename) return null;
  if (filename.startsWith('http')) return filename;
  return `${API_BASE}/api/move_images/${filename}`;
}

export default function RoutesListPage() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState<MoveRouteSummary[]>([]);
  const [pagination, setPagination] = useState<MoveRoutesPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState<RouteType | 'all'>('all');
  const [sort, setSort] = useState<SortKey>('popular');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [recommendedRoutes, setRecommendedRoutes] = useState<MoveRouteSummary[]>([]);
  const [recommendation, setRecommendation] = useState<Recommendations['recommendation'] | null>(null);
  const [recoOpen, setRecoOpen] = useState(false);
  const [canCreateRoutes, setCanCreateRoutes] = useState(false);

  useEffect(() => {
    Promise.all([
      fetchRecommendations().catch(() => null),
      fetchRouteCreatorPermission().catch(() => false),
    ]).then(([data, canCreate]) => {
      setRecommendedRoutes(data?.routes || []);
      setRecommendation(data?.recommendation ?? null);
      setCanCreateRoutes(Boolean(canCreate));
    });
  }, []);

  useEffect(() => {
    setPage(1);
  }, [tipo, sort, query]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const filters: { tipo?: RouteType; sort?: 'popular' | 'rating' | 'recent' | 'distance'; q?: string; page: number; limit: number } = {
          page,
          limit: 10,
        };
        if (tipo !== 'all') filters.tipo = tipo;
        if (query.trim()) filters.q = query.trim();
        if (sort === 'popular') filters.sort = 'popular';
        if (sort === 'rating') filters.sort = 'rating';
        if (sort === 'new') filters.sort = 'recent';
        const data = await fetchRoutesPage(filters);
        setRoutes(data.routes);
        setPagination(data.pagination);
      } catch (e: any) {
        setError(e?.response?.data?.error || 'Error cargando rutas');
      } finally {
        setLoading(false);
      }
    })();
  }, [tipo, sort, query, page]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return routes;
    return routes.filter(r => r.titulo.toLowerCase().includes(q));
  }, [routes, query]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sort === 'popular') arr.sort((a, b) => b.votos_count - a.votos_count);
    return arr;
  }, [filtered, sort]);
  const mainRecommendation = recommendedRoutes[0] || null;
  const alternativeRecommendations = recommendedRoutes.slice(1, 3);
  const showRecommendation = Boolean(mainRecommendation && page === 1 && !query.trim());

  return (
    <div className={`move-app ${styles.page}`}>
      <header className={styles.hero}>
        <div className={styles.heroBg} />
        <div className={styles.heroOrb} />
        <div className={styles.heroInner}>
          <p className={styles.greetingLabel}>Descubre</p>
          <h1 className={styles.greetingName}>Rutas</h1>
          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, marginTop: 4 }}>
            {sorted.length} {sorted.length === 1 ? 'ruta' : 'rutas'} disponibles
          </p>
        </div>
      </header>

      <div style={{ marginTop: 16 }}>
        <div className={styles.searchWrap}>
          <Search size={18} className={styles.searchIcon} />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar ruta, lugar…"
            className={styles.searchInput}
          />
          <button className={styles.searchAction} aria-label="Filtros">
            <SlidersHorizontal size={16} />
          </button>
        </div>

        <div
          className={styles.aiBanner}
          onClick={() => canCreateRoutes && navigate('/move/routes/new')}
          style={!canCreateRoutes ? { opacity: 0.72, cursor: 'default' } : undefined}
        >
          <div className={styles.aiBannerIcon}><Sparkles size={20} /></div>
          <div className={styles.aiBannerText}>
            <p className={styles.aiBannerTitle}>{canCreateRoutes ? 'Crear o importar ruta' : 'Rutas gestionadas por Human'}</p>
            <p className={styles.aiBannerSub}>
              {canCreateRoutes ? 'Dibuja en el mapa, sube un GPX o añade imagen' : 'Pide permisos al equipo si quieres publicar rutas'}
            </p>
          </div>
          {canCreateRoutes && <Plus size={20} className={styles.aiBannerPlus} />}
        </div>

        <div className={styles.pillRow} style={{ marginTop: 16 }}>
          {TYPES.map(t => (
            <button
              key={t.key}
              className={`${styles.pill} ${tipo === t.key ? styles.pillActive : ''}`}
              onClick={() => setTipo(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className={styles.sortRow}>
          <span className={styles.sortLabel}>Ordenar:</span>
          {(['popular', 'rating', 'new'] as SortKey[]).map(s => (
            <button
              key={s}
              className={`${styles.sortBtn} ${sort === s ? styles.sortActive : ''}`}
              onClick={() => setSort(s)}
            >
              {s === 'popular' ? 'Popular' : s === 'rating' ? 'Valoración' : 'Nuevas'}
            </button>
          ))}
        </div>
      </div>

      <section className={styles.section} style={{ paddingTop: '1rem' }}>
        {showRecommendation && mainRecommendation && (
          <div style={{ marginBottom: 18 }}>
            <button className={styles.recoBubble} type="button" onClick={() => setRecoOpen(open => !open)}>
              <span className={styles.recoBubbleIcon}><Sparkles size={16} /></span>
              <span className={styles.recoBubbleText}>
                <strong>{recommendation?.title || 'Ruta recomendada por IA'}</strong>
                <small>{mainRecommendation.titulo} · {mainRecommendation.recommendation_score ?? 0}% match</small>
              </span>
              <ChevronRight size={17} className={recoOpen ? styles.recoBubbleChevronOpen : styles.recoBubbleChevron} />
            </button>

            {recoOpen && (
              <div className={styles.recoPopover}>
                <div className={styles.recoPopoverImg}>
                  {imageUrl(mainRecommendation.imagen_principal)
                    ? <img src={imageUrl(mainRecommendation.imagen_principal) || ''} alt={mainRecommendation.titulo} />
                    : <div className={styles.recoHeroFallback} />
                  }
                </div>
                <div className={styles.recoPopoverBody}>
                  <p className={styles.recoPopoverTitle}>{mainRecommendation.titulo}</p>
                  <p className={styles.recoReason}>{mainRecommendation.recommendation_reason || recommendation?.subtitle}</p>
                  <div className={styles.recoMeta}>
                    <span>{formatDistance(mainRecommendation.distancia_m)}</span>
                    <span>+{mainRecommendation.desnivel_m} m</span>
                    <span>{mainRecommendation.duracion_min} min</span>
                  </div>
                  <button className={styles.recoAction} type="button" onClick={() => navigate(`/move/routes/${mainRecommendation.route_id}`)}>
                    Ver ruta
                  </button>
                </div>
              </div>
            )}

            {alternativeRecommendations.length > 0 && (
              <div className={styles.recoAlternatives}>
                {alternativeRecommendations.map(route => (
                  <button
                    key={route.route_id}
                    type="button"
                    onClick={() => navigate(`/move/routes/${route.route_id}`)}
                  >
                    <span>{route.titulo}</span>
                    <small>{route.recommendation_reason || `${formatDistance(route.distancia_m)} · +${route.desnivel_m} m`}</small>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loading && <div className={styles.empty}>Cargando…</div>}
        {error && <div className={styles.empty}>{error}</div>}

        {!loading && sorted.length === 0 && (
          <div className={styles.card} style={{ textAlign: 'center', padding: '2rem 1rem' }}>
            <Search size={32} color="hsl(200 15% 60%)" style={{ marginBottom: 12 }} />
            <p style={{ fontFamily: 'Sora, Inter, sans-serif', fontWeight: 700, fontSize: 14, margin: 0 }}>
              Sin resultados
            </p>
            <p style={{ fontSize: 12, color: 'var(--move-muted-fg)', marginTop: 4 }}>
              Prueba con otros filtros o crea una con IA
            </p>
          </div>
        )}

        <div className={styles.cardList}>
          {sorted.map(r => {
            const img = imageUrl(r.imagen_principal);
            return (
              <div
                key={r.route_id}
                className={styles.routeCard}
                onClick={() => navigate(`/move/routes/${r.route_id}`)}
              >
                <div className={styles.routeCardImg}>
                  {img
                    ? <img src={img} alt={r.titulo} />
                    : <div className={styles.routeImgFallback} />
                  }
                  <div className={styles.routeCardImgBadges}>
                    <span className={`${styles.tag} ${styles.tagPrimary}`}>{r.tipo}</span>
                    <span className={styles.routeRatingBadge}>
                      <Star size={11} fill="hsl(42 95% 50%)" color="hsl(42 95% 50%)" />
                      {Number(r.rating_avg).toFixed(1)}
                    </span>
                  </div>
                </div>
                <div className={styles.routeCardBody}>
                  <p className={styles.routeCardTitle}>{r.titulo}</p>
                  {r.ubicacion_inicio_nombre && (
                    <p className={styles.cardSubtitle} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={12} /> {r.ubicacion_inicio_nombre}
                    </p>
                  )}
                  <div className={styles.cardMeta}>
                    <span>{formatDistance(r.distancia_m)}</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <TrendingUp size={12} /> +{r.desnivel_m} m
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={12} /> {r.duracion_min} min
                    </span>
                    <span className={`${styles.tag} ${styles.tagAccent}`}>{difficultyLabel(r.dificultad)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && pagination && pagination.totalPages > 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '44px 1fr 44px', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <button
              className={styles.btnGhost}
              type="button"
              aria-label="Página anterior"
              disabled={!pagination.hasPrev}
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              style={{ padding: 0, height: 44, justifyContent: 'center', display: 'inline-flex', alignItems: 'center' }}
            >
              <ChevronLeft size={18} />
            </button>
            <p style={{ margin: 0, textAlign: 'center', fontSize: 12, color: 'var(--move-muted-fg)', fontWeight: 700 }}>
              Página {pagination.page} de {pagination.totalPages} · {pagination.total} rutas
            </p>
            <button
              className={styles.btnGhost}
              type="button"
              aria-label="Página siguiente"
              disabled={!pagination.hasNext}
              onClick={() => setPage(prev => prev + 1)}
              style={{ padding: 0, height: 44, justifyContent: 'center', display: 'inline-flex', alignItems: 'center' }}
            >
              <ChevronRight size={18} />
            </button>
          </div>
        )}
      </section>

    </div>
  );
}
